import { Box, Container, Flex, Text } from "@radix-ui/themes";
import { GIFT_LINK_PATH } from "@shared/domain/giftLink";
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouteTheme } from "./hooks/useRouteTheme";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { REFERRALS_ENABLED } from "./config/featureFlags";
import { SUIKAMON_SPECIES } from "@shared/domain/speciesConfig";
import { LoginButton } from "./components/shared/LoginButton";
import { useAccountId } from "./auth/useAccountId";
import { useWalletConnection } from "./auth/useWalletConnection";
import { FeedbackButton } from "./components/shared/FeedbackButton";
import { AnnouncementBanner } from "./components/AnnouncementBanner";
import { ANNOUNCEMENT_CONFIG } from "@shared/domain/announcementConfig";
import { detectSuiEnv, type SuiRuntimeEnv } from "./utils/sui-env";
import { useAnalytics } from "./hooks/useAnalytics";
import { useAppVersionRefresh } from "./hooks/useAppVersionRefresh";
import { clearAllPvpStorage } from "./api/pvpApi";
import { SeasonPopup, shouldShowSeasonPopup } from "./components/SeasonPopup";
import { PromoMessages, shouldShowSocialPromo } from "./components/PromoMessages";
import { hasPlayedEnoughForNudges, PLAYER_ACTION_EVENT } from "./lib/playerEngagement";
import {
  WelcomeActivationModal,
  shouldShowActivation,
  markActivationShown,
} from "./components/WelcomeActivationModal";
import { BATTLE_LAB_PATH } from "./components/game-shell/gameNavConfig";
import { DailyLoginPopup } from "./components/DailyLoginPopup";
import { SUIKAMON_APP_VERSION } from "@shared/domain/appConfig";
import { ConsentBar } from "./components/shared/ConsentBar";
import { resetAnalyticsConsent } from "./lib/analyticsConsent";
import {
  PlayFirstSuikamonPopup,
  shouldShowPlayPopup,
  markPlayPopupShown,
} from "./components/PlayFirstSuikamonPopup";
import {
  PlayFirstSuikamonBanner,
  PlayFirstSuikamonSpacer,
} from "./components/shared/PlayFirstSuikamonBanner";
import { useHasNoSuikamon } from "./hooks/useHasNoSuikamon";
import { getPlayFreePath, getSpeciesFromPath } from "./utils/playFreeLink";
import { useQuery } from "@tanstack/react-query";
import { checkUserHasAnyPets, fetchTrainerProfile } from "./api/petBackendApi";
import type { PageKey, AppNavigationTarget } from "./appNavTypes";
export type { PageKey } from "./appNavTypes";
import { GameShell } from "./components/game-shell/GameShell";
import { LanguageSelector } from "./components/i18n/LanguageSelector";
import { useT } from "./i18n/i18nContext";
import { lazyWithReload } from "./utils/lazyWithReload";
import { CAMPAIGN_LINKS, campaignLandingUrl, rememberCampaignLink, type CampaignLinkKey } from "./lib/campaignSource";



const HomePage = lazyWithReload(() => import("./pages/HomePage").then(module => ({ default: module.HomePage })), "HomePage");
const PlayPage = lazyWithReload(() => import("./pages/PlayPage").then(module => ({ default: module.PlayPage })), "PlayPage");
const DemoPage = lazyWithReload(() => import("./pages/DemoPage").then(module => ({ default: module.DemoPage })), "DemoPage");
const PlayFreePage = lazyWithReload(() => import("./pages/PlayFreePage").then(module => ({ default: module.PlayFreePage })), "PlayFreePage");
const GiftPage = lazyWithReload(() => import("./pages/GiftPage").then(module => ({ default: module.GiftPage })), "GiftPage");
const DiscoverPage = lazyWithReload(() => import("./pages/DiscoverPage").then(module => ({ default: module.DiscoverPage })), "DiscoverPage");
const RankingsPage = lazyWithReload(() => import("./pages/RankingsPage").then(module => ({ default: module.RankingsPage })), "RankingsPage");
const HelpPage = lazyWithReload(() => import("./pages/HelpPage").then(module => ({ default: module.HelpPage })), "HelpPage");
const PvpHelpPage = lazyWithReload(() => import("./pages/PvpHelpPage").then(module => ({ default: module.PvpHelpPage })), "PvpHelpPage");
const BattleCardsPage = lazyWithReload(() => import("./pages/BattleCardsPage").then(module => ({ default: module.BattleCardsPage })), "BattleCardsPage");
const PetCardsPage = lazyWithReload(() => import("./pages/PetCardsPage").then(module => ({ default: module.PetCardsPage })), "PetCardsPage");
const DailyRewardsPage = lazyWithReload(() => import("./pages/DailyRewardsPage").then(module => ({ default: module.DailyRewardsPage })), "DailyRewardsPage");
const StorePage = lazyWithReload(() => import("./pages/StorePage").then(module => ({ default: module.StorePage })), "StorePage");
const PvpBattlePage = lazyWithReload(() => import("./pages/PvpBattlePage").then(module => ({ default: module.PvpBattlePage })), "PvpBattlePage");
const PvpTrialPage = lazyWithReload(() => import("./pages/PvpTrialPage").then(module => ({ default: module.PvpTrialPage })), "PvpTrialPage");
const PvpBattleLabPage = lazyWithReload(() => import("./pages/PvpBattleLabPage").then(module => ({ default: module.PvpBattleLabPage })), "PvpBattleLabPage");
const PvpRankedPage = lazyWithReload(() => import("./pages/PvpRankedPage").then(module => ({ default: module.PvpRankedPage })), "PvpRankedPage");
const PvpSquadRankedPage = lazyWithReload(() => import("./pages/PvpSquadRankedPage").then(module => ({ default: module.PvpSquadRankedPage })), "PvpSquadRankedPage");
const CampaignPage = lazyWithReload(() => import("./pages/CampaignPage").then(module => ({ default: module.CampaignPage })), "CampaignPage");
const StoryPage = lazyWithReload(() => import("./pages/StoryPage").then(module => ({ default: module.StoryPage })), "StoryPage");
const WhitepaperPage = lazyWithReload(() => import("./pages/WhitepaperPage").then(module => ({ default: module.WhitepaperPage })), "WhitepaperPage");
const TermsPage = lazyWithReload(() => import("./pages/TermsPage").then(module => ({ default: module.TermsPage })), "TermsPage");
const PrivacyPage = lazyWithReload(() => import("./pages/PrivacyPage").then(module => ({ default: module.PrivacyPage })), "PrivacyPage");
const PetDetailPage = lazyWithReload(() => import("./pages/PetDetailPage").then(module => ({ default: module.PetDetailPage })), "PetDetailPage");
const TrainerProfilePage = lazyWithReload(() => import("./pages/TrainerProfilePage").then(module => ({ default: module.TrainerProfilePage })), "TrainerProfilePage");
const AdminPage = lazyWithReload(() => import("./pages/AdminPage").then(module => ({ default: module.AdminPage })), "AdminPage");
const ReferralsPage = lazyWithReload(() => import("./pages/ReferralsPage").then(module => ({ default: module.ReferralsPage })), "ReferralsPage");
const PvpScreenshotPage = lazyWithReload(() => import("./pages/PvpScreenshotPage").then(module => ({ default: module.PvpScreenshotPage })), "PvpScreenshotPage");
const SpeciesPage = lazyWithReload(() => import("./pages/SpeciesPage").then(module => ({ default: module.SpeciesPage })), "SpeciesPage");
const MediaPage = lazyWithReload(() => import("./pages/MediaPage").then(module => ({ default: module.MediaPage })), "MediaPage");
const BusinessBriefingPage = lazyWithReload(() => import("./pages/BusinessBriefingPage").then(module => ({ default: module.BusinessBriefingPage })), "BusinessBriefingPage");
const NewsPage = lazyWithReload(() => import("./pages/NewsPage").then(module => ({ default: module.NewsPage })), "NewsPage");
const NewsArticlePage = lazyWithReload(() => import("./pages/NewsArticlePage").then(module => ({ default: module.NewsArticlePage })), "NewsArticlePage");
const CollectionPage = lazyWithReload(() => import("./pages/CollectionPage").then(module => ({ default: module.CollectionPage })), "CollectionPage");
const SuikadexPage = lazyWithReload(() => import("./pages/SuikadexPage").then(module => ({ default: module.SuikadexPage })), "SuikadexPage");
const UserProfilePage = lazyWithReload(() => import("./pages/UserProfilePage").then(module => ({ default: module.UserProfilePage })), "UserProfilePage");
const NotFoundPage = lazyWithReload(() => import("./pages/NotFoundPage").then(module => ({ default: module.NotFoundPage })), "NotFoundPage");
const MinigamesTestPage = lazyWithReload(() => import("./pages/MinigamesTestPage").then(module => ({ default: module.MinigamesTestPage })), "MinigamesTestPage");
const SuikamonCssPage = lazyWithReload(() => import("./pages/SuikamonCssPage").then(module => ({ default: module.SuikamonCssPage })), "SuikamonCssPage");

function PageFallback() {
  const t = useT();
  return (
    <Flex align="center" justify="center" style={{ minHeight: "40vh" }}>
      <Box>
        <Text size="3" style={{ color: "rgba(255,255,255,0.7)" }}>
          {t("navigation.app.loading")}
        </Text>
      </Box>
    </Flex>
  );
}

/**
 * Derive the current page key from the React Router location
 */
function useActivePage(): PageKey {
  const location = useLocation();
  const pathname = location.pathname;

  if (pathname === "/" || pathname === "") return "home";
  // BEFORE the /suikamon/ prefix below, which would otherwise swallow it: a pet's cards are
  // not the pet's public detail page, and calling them one drops the game shell (pet-detail is
  // a website route) — leaving the cards screen with no rail, no tab bar and no way back.
  if (pathname.startsWith("/suikamon/") && pathname.endsWith("/cards")) return "pet-cards";
  if (pathname === "/store") return "store";
  if (pathname.startsWith("/suikamon/") || pathname.startsWith("/pet/")) return "pet-detail";
  if (pathname === "/daily-rewards") return "daily-rewards";
  if (pathname.startsWith("/trainer/")) return "trainer-profile";
  // A gift link is onboarding like the free starter: the same shell, a winner who may have
  // no account yet.
  if (pathname === "/play-free" || pathname.startsWith(`${GIFT_LINK_PATH}/`)) return "play-free";
  if (pathname === "/play" || pathname.startsWith("/play/")) return "play";
  if (pathname === "/adopt") return "adopt";
  if (pathname === "/profile") return "profile";
  if (pathname === "/collection") return "collection";
  if (pathname === "/suikadex") return "suikadex";
  if (pathname === "/rankings") return "rankings";
  if (pathname === "/trainers") return "trainers";
  if (pathname === "/help") return "help";
  if (pathname === "/pvp-guide") return "pvp-guide";
  if (pathname === "/battle-guide") return "battle-guide";
  if (pathname === "/pvp-lab") return "pvp-lab";
  if (pathname === "/pvp-trial") return "pvp-trial";
  if (pathname === "/pvp-ranked") return "pvp-ranked";
  if (pathname === "/pvp-squad-ranked") return "pvp-squad-ranked";
  if (pathname === "/campaign") return "campaign";
  if (pathname === "/story") return "story";
  if (pathname === "/whitepaper") return "whitepaper";
  if (pathname === "/demo") return "demo";
  if (pathname === "/terms") return "terms";
  if (pathname === "/privacy") return "privacy";
  if (pathname === "/media") return "media";
  if (pathname === "/news" || pathname.startsWith("/news/")) return "news";
  // Admin/metrics/debug pages should use standard top padding (not the home variant)
  if (pathname === "/admin" || pathname === "/debug" || pathname === "/metrics") return "metrics";

  return "home";
}

/**
 * External redirect component for Telegram community link
 */
/** Serves combined static docs + live briefing as plain text at /ai */
function AiDocsPage() {
  const [text, setText] = useState("");

  useEffect(() => {
    // Strip all app styling — this page is raw text only
    document.documentElement.style.cssText = "all:unset";
    document.body.style.cssText = "all:unset;white-space:pre-wrap;font-family:monospace;font-size:14px;padding:1em";
    document.title = "Suikamon AI Documentation";

    const apiUrl = import.meta.env.VITE_API_URL;
    if (apiUrl) {
      fetch(`${apiUrl}/api/v1/llms`)
        .then(r => r.text())
        .then(setText)
        .catch(() => setText("Failed to load documentation."));
    }

    return () => {
      document.documentElement.style.cssText = "";
      document.body.style.cssText = "";
    };
  }, []);

  return <>{text}</>;
}

/**
 * Vanity short links for the off-site community profiles. They exist so a campaign can print
 * a suikamon.com URL instead of a bare platform link — the destination stays changeable here
 * without reprinting posts that are already scheduled.
 */
const SOCIAL_PROFILES = {
  telegram: { url: "https://t.me/suikamongame", label: "Telegram" },
  instagram: { url: "https://www.instagram.com/suikamongame/", label: "Instagram" },
} as const;

interface SocialRedirectProps {
  profile: keyof typeof SOCIAL_PROFILES;
}

function SocialRedirect({ profile }: SocialRedirectProps) {
  const t = useT();
  const { url, label } = SOCIAL_PROFILES[profile];

  useEffect(() => {
    window.location.href = url;
  }, [url]);

  return (
    <Flex className="social-redirect">
      <Box>
        <Text size="3" className="social-redirect__text">
          {t("navigation.app.redirecting", { site: label })}
        </Text>
      </Box>
    </Flex>
  );
}

const CAMPAIGN_LINK_KEYS = Object.keys(CAMPAIGN_LINKS) as CampaignLinkKey[];

interface CampaignRedirectProps {
  link: CampaignLinkKey;
}

function CampaignRedirect({ link }: CampaignRedirectProps) {
  rememberCampaignLink(link);
  return <Navigate to={campaignLandingUrl(link)} replace />;
}

/**
 * The onboarding flows themselves — a "start playing" prompt on top of them is noise, and on
 * /adopt it competes with the page's own adoption CTA.
 */
const ONBOARDING_FLOW_PAGES: PageKey[] = ["play-free", "adopt"];

/**
 * Pages where the sticky play banner stands down: the onboarding flows above plus the
 * immersive game screens, which own the bottom of the screen (sticky battle CTAs) and already
 * gate pet-less players with the full StartJourneyPanel. /play is that panel for a pet-less
 * player, so a "Play free" bar under its "Start Now" asked the same thing twice.
 */
const BANNER_HIDDEN_PAGES: PageKey[] = [
  ...ONBOARDING_FLOW_PAGES,
  "play",
  "campaign",
  "pvp-lab",
  "pvp-trial",
  "pvp-ranked",
  "pvp-squad-ranked",
];

/**
 * Main app shell with navigation and routing
 */
function AppContent() {
  const t = useT();
  // -1 indicates no species selected yet (start on "Available Pets" list)
  const [selectedSpeciesId, setSelectedSpeciesId] = useState<number>(-1);
  const [suiEnv, setSuiEnv] = useState<SuiRuntimeEnv>(() => detectSuiEnv());
  const [showSeasonPopup, setShowSeasonPopup] = useState(false);
  const [showSocialPromo, setShowSocialPromo] = useState(false);
  // Dismiss latch for the global "play your first Suikamon" nudge (per-session; localStorage
  // handles the cross-reload cooldown). Set true the moment the player closes or acts on it.
  const [playPopupDismissed, setPlayPopupDismissed] = useState(false);
  const {
    isAuthenticated,
    accountId,
    address: accountAddress,
    activationPending,
    dismissActivation,
  } = useAccountId();
  // "Logged in" = a signed-in (Supabase) account. No wallet provider is mounted in web2.
  const isLoggedIn = isAuthenticated;
  // Guest = a pre-identity trial player (free starter pet, no login). Guests get a
  // "Sign up to keep your Suikamon" prompt instead of the wallet menu — never the
  // address/Add-funds chrome, which would prompt a wallet connect they can't complete.
  const { actingAddress } = useWalletConnection();
  const reactRouterNavigate = useNavigate();
  const location = useLocation();
  const activePage = useActivePage();
  useRouteTheme();

  useAppVersionRefresh();

  // One-time "cold open → Play" forward. A player who opens the app already connected wants to
  // start playing, not land on the marketing home page. We only forward a fresh page load that
  // LANDED on home ("/"): opening directly on Play / Campaign / Battle, or clicking Home later
  // from inside the app, is a deliberate destination and must never be hijacked. Because wallet
  // auto-connect is deferred, `isLoggedIn` may flip true a beat after mount — so we wait for it,
  // but abort the moment the player navigates away from home themselves.
  const coldOpenLandedHome = useRef(location.pathname === "/");
  const autoForwardDone = useRef(false);
  useEffect(() => {
    if (autoForwardDone.current) return;
    // A load that didn't start on home is a deliberate destination — never forward it.
    if (!coldOpenLandedHome.current) {
      autoForwardDone.current = true;
      return;
    }
    // The player left home before identity resolved — respect that, stop waiting to forward.
    if (location.pathname !== "/") {
      autoForwardDone.current = true;
      return;
    }
    // Still sitting on the home landing: once a connected identity resolves, send them to Play.
    if (isLoggedIn) {
      autoForwardDone.current = true;
      reactRouterNavigate("/play", { replace: true });
    }
  }, [location.pathname, isLoggedIn, reactRouterNavigate]);

  // Viewport width determines menu display (prioritized over user agent)

  // Detect Sui environment (mobile, wallet standard, Slush in-app browser)
  useEffect(() => {
    const checkEnv = () => {
      setSuiEnv(detectSuiEnv());
    };

    checkEnv();
    // Re-check on resize to detect orientation changes that might affect UI
    window.addEventListener('resize', checkEnv);

    return () => window.removeEventListener('resize', checkEnv);
  }, []);

  // Handle hash navigation on page load and when location changes
  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      // Wait for page to fully render, then scroll to anchor
      // Use setTimeout to wait for lazy-loaded components
      const timeoutId = setTimeout(() => {
        const element = document.getElementById(hash.substring(1));
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [location]);

  // Own scroll position ourselves: the browser's automatic scroll restoration
  // would otherwise re-apply a previous entry's (often bottom-of-page) offset
  // *after* our lazy-loaded route content finishes mounting, making a freshly
  // navigated page "jump to the end" once it loads. We reset to top explicitly
  // on every navigation instead.
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Scroll to top on every navigation, regardless of how it was triggered
  // (custom navigate(), <Link>, or a direct useNavigate() call in a
  // page/component). This runs in multiple phases because lazy mounts and
  // focus-restoration can re-scroll the page *after* the first reset.
  // Hash routes are excluded so in-page anchors still work.
  useEffect(() => {
    if (location.hash) return;

    const resetScrollTop = () => {
      const active = document.activeElement;
      if (active instanceof HTMLElement) {
        active.blur();
      }

      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      const root = document.getElementById('root');
      if (root) {
        root.scrollTop = 0;
      }
    };

    // Immediate + next frame + short delayed pass to beat late layout/focus jumps.
    resetScrollTop();
    const frame = requestAnimationFrame(resetScrollTop);
    const timeout = window.setTimeout(resetScrollTop, 120);

    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
    };
  }, [location.pathname, location.search, location.key, location.hash]);

  // Reset species selection when navigating to /adopt
  useEffect(() => {
    if (location.pathname === '/adopt') {
      setSelectedSpeciesId(-1);
    }
  }, [location.pathname]);

  // Clear PVP session storage when the player signs out or switches account
  const [previousWalletAddress, setPreviousWalletAddress] = useState<string | undefined>();

  useEffect(() => {
    const currentAddress = accountAddress ?? undefined;

    // If the account changed or signed out, clear PVP storage
    if (previousWalletAddress && previousWalletAddress !== currentAddress) {
      console.log('[PVP] Account changed or signed out, clearing PVP session');
      clearAllPvpStorage();
    }

    setPreviousWalletAddress(currentAddress);
  }, [accountAddress, previousWalletAddress]);

  /**
   * **NOTHING OPTIONAL INTERRUPTS A PLAYER WHO HAS NOT PLAYED YET.**
   *
   * Both popups below used to fire on the appearance of an identity — which for a new
   * trainer is the instant their adoption finishes. The first thing they saw, before
   * touching their own Suikamon once, was a modal asking them to go join a Telegram group.
   *
   * They now wait for `hasPlayedEnoughForNudges()`: a few real care actions or battle turns,
   * counted at the choke points those pass through. `nudgesUnlocked` re-reads on the
   * `PLAYER_ACTION_EVENT` rather than only on an identity change, because an identity change
   * is exactly the moment these must stop reacting to — without the listener the count would
   * never be re-read within a session and a nudge could not appear until a reload.
   */
  const [nudgesUnlocked, setNudgesUnlocked] = useState(hasPlayedEnoughForNudges);

  useEffect(() => {
    const recheck = () => setNudgesUnlocked(hasPlayedEnoughForNudges());
    window.addEventListener(PLAYER_ACTION_EVENT, recheck);
    return () => window.removeEventListener(PLAYER_ACTION_EVENT, recheck);
  }, []);

  // Season popup: only surface once a player is signed in, has actually played,
  // and only if they haven't dismissed the current season's popup yet.
  useEffect(() => {
    const eligible = shouldShowSeasonPopup();
    if (accountAddress && eligible && nudgesUnlocked) {
      setShowSeasonPopup(true);
    }
  }, [accountAddress, nudgesUnlocked]);

  // Debug helper — call window.suikamonShowSeasonPopup() from console to force-show.
  useEffect(() => {
    (window as unknown as { suikamonShowSeasonPopup?: () => void }).suikamonShowSeasonPopup = () => {
      setShowSeasonPopup(true);
    };
  }, []);

  // One-time social promo (Telegram + X, spoken by the player's Suikamon): an acting
  // identity — a logged-in identity or a free-play guest — is necessary but NOT sufficient.
  // "Come join my clan on Telegram" is an invitation, and an invitation handed to somebody
  // who has not played the game yet is one they have no reason to accept; it waits until
  // they have looked after their Suikamon or fought with it. See `nudgesUnlocked` above.
  useEffect(() => {
    if (actingAddress && nudgesUnlocked && shouldShowSocialPromo()) {
      setShowSocialPromo(true);
    }
  }, [actingAddress, nudgesUnlocked]);

  // Debug helper — call window.suikamonShowSocialPromo() from console to force-show.
  useEffect(() => {
    (window as unknown as { suikamonShowSocialPromo?: () => void }).suikamonShowSocialPromo = () => {
      setShowSocialPromo(true);
    };
  }, []);

  const selectedSpecies = useMemo(
    () => SUIKAMON_SPECIES.find((species) => species.id === selectedSpeciesId) ?? SUIKAMON_SPECIES[0] ?? null,
    [selectedSpeciesId],
  );

  /**
   * Custom navigate function that wraps React Router's navigate
   * Maintains backward compatibility with existing component APIs
   */
  const navigate = useCallback(
    (page: AppNavigationTarget, options?: { speciesId?: number; petId?: string; walletAddress?: string }) => {
      if (page.startsWith("/")) {
        reactRouterNavigate(page);
        return;
      }

      // Handle species selection for adopt page
      if (page === "adopt") {
        if (options?.speciesId !== undefined) {
          setSelectedSpeciesId(options.speciesId);
        } else {
          setSelectedSpeciesId(-1);
        }
      }

      // Navigate using React Router
      if (page === "home") {
        reactRouterNavigate("/");
      } else if (page === "demo") {
        // The care demo is its own page again; the home page keeps only a tile that opens it.
        reactRouterNavigate("/demo");
      } else if (page === "pet-detail" && options?.petId) {
        reactRouterNavigate(`/suikamon/${options.petId}`);
      } else if (page === "trainer-profile" && options?.walletAddress) {
        reactRouterNavigate(`/trainer/${options.walletAddress}`);
      } else if (page === "play") {
        if (options?.petId) {
          reactRouterNavigate(`/play/${options.petId}`);
        } else {
          reactRouterNavigate("/play");
        }
      } else if (page === "rankings") {
        reactRouterNavigate("/rankings");
      } else if (page === "trainers") {
        reactRouterNavigate("/trainers");
      } else if (page === "adopt" || page === "help" || page === "pvp-lab" || page === "pvp-trial" || page === "pvp-ranked" || page === "pvp-squad-ranked" || page === "campaign" || page === "story" || page === "whitepaper" || page === "terms" || page === "privacy" || page === "news" || page === "battle-guide" || page === "pvp-guide" || page === "collection" || page === "suikadex" || page === "metrics" || page === "media" || page === "play-free") {
        reactRouterNavigate(`/${page}`);
      }
    },
    [reactRouterNavigate],
  );

  // New-wallet activation flow: a one-time welcome modal shown right after a brand-new
  // wallet logs in (transient `activationPending` from /verify `is_new`), only while it
  // owns zero Suikamons, and only once per account (localStorage guard). Identity/UX only.
  const activationEligible =
    activationPending && !!accountId && !!accountAddress && shouldShowActivation(accountId);

  // Zero-Suikamons gate — use the boolean has-pets endpoint (cheaper than fetching the list).
  const { data: activationHasPets } = useQuery({
    queryKey: ["activationHasPets", accountAddress],
    queryFn: () => checkUserHasAnyPets(accountAddress!),
    enabled: activationEligible,
    staleTime: 30_000,
  });

  // Greeting name (optional) — same trainer-profile source the account menu uses.
  const { data: activationProfile } = useQuery({
    queryKey: ["activationProfile", accountAddress],
    queryFn: () => fetchTrainerProfile(accountAddress!),
    enabled: activationEligible,
    staleTime: 30_000,
  });

  const showActivation = activationEligible && activationHasPets === false;

  const dismissActivationFlow = useCallback(() => {
    if (accountId) markActivationShown(accountId);
    dismissActivation();
  }, [accountId, dismissActivation]);

  const handleActivationPlayLab = useCallback(() => {
    dismissActivationFlow();
    navigate(BATTLE_LAB_PATH);
  }, [dismissActivationFlow, navigate]);

  const handleActivationAdopt = useCallback(() => {
    dismissActivationFlow();
    // Web2: the first Suikamon is the free starter — never the (parked) paid adopt page.
    navigate("/play-free");
  }, [dismissActivationFlow, navigate]);

  // The single "does this visitor own zero Suikamon?" signal, shared by every onboarding
  // surface (welcome popup + sticky play banner) so they appear and disappear together.
  const hasNoSuikamon = useHasNoSuikamon();

  // The species landing pages (`/bireon`) are a common first touch from search — when the
  // visitor is on one, the onboarding banner names that Suikamon and pre-picks it.
  const routeSpecies = useMemo(() => getSpeciesFromPath(location.pathname), [location.pathname]);
  // The home page itself ("/" only — unknown paths also resolve to the "home" key) skips both
  // "play your first Suikamon" nudges: its hero IS the starter pick, so a nudge there is a
  // second button for the same tap.
  const isHomeRoute = location.pathname === "/";

  // Global "play your first Suikamon" nudge — shown on any page (except the onboarding flows
  // themselves) to a visitor who owns zero Suikamons. Gated by the acting identity (or guest)
  // address with a localStorage cooldown so it stays a nudge, not a nag, and yields to the
  // higher-priority season / new-wallet activation popups.
  const playPopupEligible =
    !!actingAddress &&
    !ONBOARDING_FLOW_PAGES.includes(activePage) &&
    !isHomeRoute &&
    !playPopupDismissed &&
    shouldShowPlayPopup(actingAddress);

  const dismissPlayPopup = useCallback(() => {
    if (actingAddress) markPlayPopupShown(actingAddress);
    setPlayPopupDismissed(true);
  }, [actingAddress]);

  const handlePlayPopupStart = useCallback(() => {
    dismissPlayPopup();
    // Router-level path (it carries a ?species= query), so bypass the PageKey navigate wrapper.
    reactRouterNavigate(getPlayFreePath(routeSpecies?.id));
  }, [dismissPlayPopup, reactRouterNavigate, routeSpecies]);

  // The sticky bottom banner is the always-visible counterpart to the popup: it rides along on
  // every content page (home, news, species, help, rankings…) so a pet-less visitor is never
  // more than one tap from playing. It stands down on the onboarding flows it would duplicate
  // and on the immersive game screens, which own the bottom of the screen and already show the
  // full StartJourneyPanel gate.
  const showPlayBanner = hasNoSuikamon && !isHomeRoute && !BANNER_HIDDEN_PAGES.includes(activePage);

  // Reset species selection when navigating to adopt page
  useEffect(() => {
    if (activePage === "adopt") {
      // Only reset if navigating fresh (not setting via navigate function)
      // The navigate function handles species state internally
    }
  }, [activePage]);

  const handleSelectSpecies = useCallback((speciesId: number) => {
    setSelectedSpeciesId(speciesId);
  }, []);

  const focusMyPetsConsole = useCallback(() => {
    document.getElementById("suikamon-play-console")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  // GA4 page_view + sign-in instrumentation
  useAnalytics();

  // Pages that render without app shell
  const isScreenshotPage = location.pathname === '/pvp-screenshot';
  const isBattlePage = location.pathname === '/battle';
  const isBriefingPage = location.pathname === '/business-briefing';
  const isAiPage = location.pathname === '/ai';

  if (isAiPage) {
    return <AiDocsPage />;
  }

  if (isScreenshotPage) {
    return (
      <Suspense fallback={<PageFallback />}>
        <PvpScreenshotPage />
      </Suspense>
    );
  }

  if (isBriefingPage) {
    return (
      <Suspense fallback={<PageFallback />}>
        <BusinessBriefingPage />
      </Suspense>
    );
  }

  if (isBattlePage) {
    return (
      <Suspense fallback={<PageFallback />}>
        <PvpBattlePage />
      </Suspense>
    );
  }

  // The footer is hidden on the immersive game-loop pages (Play, Campaign, battles) so they
  // read as a game screen, not a scrolling website. The pet's Skills, the Store, the daily
  // rewards and the starter pick are game screens too: on a phone the Skills shelf ended in a
  // screen of empty space and then a full website footer — Terms, Privacy, "Follow the
  // signal" — under a card shop.
  const hideFooter = (
    [
      "play", "campaign", "pvp-trial", "pvp-ranked", "pvp-squad-ranked", "pvp-lab",
      "pet-cards", "store", "daily-rewards", "play-free",
    ] as PageKey[]
  ).includes(activePage);

  // Login / account control for the desktop game rail, so hiding the top nav on desktop game
  // pages loses nothing. Reuses the same auth logic/components as the top nav.
  // Signed-in players get NO rail-foot CTA: the profile avatar (GameProfileCard) opens the
  // profile page and the ☰ GameWalletMenu holds the wallet. Every signed-out state (fresh
  // visitor or guest) shows a single "Sign up" button — never a "Play" button, which duplicated
  // the Suikamon nav tab. Guest free-play stays reachable via the dialog's "Play as Guest" entry.
  const gameAccountSlot = isLoggedIn ? null : (
    <LoginButton
      className="suikamon-connect-wallet"
      triggerText={t("navigation.app.signUp")}
      guestCta
      onGuestPlay={() => navigate("/play-free")}
    />
  );

  return (
    <Flex direction="column" gap="0" className="suikamon-app-shell">
      {showSeasonPopup && <SeasonPopup onClose={() => setShowSeasonPopup(false)} />}
      {showActivation && (
        <WelcomeActivationModal
          displayName={activationProfile?.displayName}
          onPlayLab={handleActivationPlayLab}
          onAdopt={handleActivationAdopt}
          onClose={dismissActivationFlow}
        />
      )}
      {/* Daily login reward: once a day for a signed-in trainer, after any higher-priority popup. */}
      <DailyLoginPopup suppressed={showSeasonPopup || showActivation} />
      <ConsentBar />
      {showSocialPromo && !showSeasonPopup && !showActivation && (
        <PromoMessages onClose={() => setShowSocialPromo(false)} />
      )}
      {playPopupEligible && hasNoSuikamon && !showActivation && !showSeasonPopup && !showSocialPromo && (
        <PlayFirstSuikamonPopup
          species={routeSpecies}
          onPlay={handlePlayPopupStart}
          onClose={dismissPlayPopup}
        />
      )}
      {showPlayBanner && <PlayFirstSuikamonBanner species={routeSpecies} />}
      <a
        href="#main-content"
        className="suikamon-skip-link"
        onClick={(e) => {
          e.preventDefault();
          const main = document.getElementById('main-content');
          if (main) {
            main.focus();
            main.scrollIntoView({ behavior: 'smooth' });
          }
        }}
      >
        {t("navigation.app.skipLink")}
      </a>

      <AnnouncementBanner {...ANNOUNCEMENT_CONFIG} />

      {!ANNOUNCEMENT_CONFIG.enabled && <FeedbackButton raised={showPlayBanner} />}

      {/* Only "/" is the full-bleed home: species pages (/aqua) also resolve to the "home" key,
          and keying on it took their top gap away, so their header sat flush on the screen edge. */}
      <Container id="main-content" tabIndex={-1} size="4" px="4" pb="8" className={`suikamon-main${isHomeRoute ? " suikamon-main--home" : ""}`}>
        <GameShell
          activePage={activePage}
          onNavigate={navigate}
          actingAddress={actingAddress}
          accountSlot={gameAccountSlot}
        >
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<HomePage onNavigate={navigate} suiEnv={suiEnv} />} />
            <Route
              path="/play"
              element={
                <PlayPage
                  selectedSpecies={selectedSpecies}
                  onSelectSpecies={handleSelectSpecies}
                  focusConsole={focusMyPetsConsole}
                  onNavigate={navigate}
                  suiEnv={suiEnv}
                />
              }
            />
            <Route
              path="/play/:petId"
              element={
                <PlayPage
                  selectedSpecies={selectedSpecies}
                  onSelectSpecies={handleSelectSpecies}
                  focusConsole={focusMyPetsConsole}
                  onNavigate={navigate}
                  suiEnv={suiEnv}
                />
              }
            />
            <Route path="/play-free" element={<PlayFreePage />} />
            <Route path={`${GIFT_LINK_PATH}/:code`} element={<GiftPage />} />
            <Route path="/my-pets" element={<Navigate to="/play" replace />} />
            <Route path="/my-suikamons" element={<Navigate to="/play" replace />} />
            <Route path="/mint" element={<Navigate to="/adopt" replace />} />
            <Route path="/rankings" element={<RankingsPage suiEnv={suiEnv} onNavigate={navigate} initialView="suikamons" />} />
            <Route path="/trainers" element={<RankingsPage suiEnv={suiEnv} onNavigate={navigate} initialView="trainers" />} />
            <Route path="/browse" element={<Navigate to="/rankings" replace />} />
            <Route path="/suikamon/:petId" element={<PetDetailPage onNavigate={navigate} />} />
            <Route path="/pet/:petId" element={<PetDetailPage onNavigate={navigate} />} />
            <Route path="/trainer/:walletAddress" element={<TrainerProfilePage onNavigate={navigate} />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/battle-guide" element={<PvpHelpPage />} />
            <Route path="/battle-cards" element={<BattleCardsPage />} />
            <Route path="/store" element={<StorePage />} />
            {/* Per-pet loadout + shop. /battle-cards stays the rules reference; this is one pet's cards. */}
            <Route path="/suikamon/:petId/cards" element={<PetCardsPage />} />
            <Route path="/daily-rewards" element={<DailyRewardsPage />} />
            <Route path="/pvp-guide" element={<Navigate to="/battle-guide" replace />} />
            {/* The Battle Lab: preset fighters, no Suikamon needed — where Battle sends a visitor
                with nothing to train. Its old "win 4 → 75% off" reward is gone (web2 has nothing
                to buy); finishing the Lab now points at the free starter pick instead. */}
            <Route path="/pvp-lab" element={<PvpBattleLabPage />} />
            <Route path="/pvp-trial" element={<PvpTrialPage />} />
            <Route path="/pvp-ranked" element={<PvpRankedPage />} />
            <Route path="/pvp-squad-ranked" element={<PvpSquadRankedPage />} />
            <Route path="/campaign" element={<CampaignPage />} />
            <Route path="/story" element={<StoryPage />} />
            <Route path="/battle" element={<PvpBattlePage />} />
            <Route path="/whitepaper" element={<WhitepaperPage />} />
            {/* Referrals are off (shared/domain/featureFlags.ts). The route stays mapped
                rather than removed so an invite link already in the wild lands on the home
                page instead of a 404 — and so re-enabling is one flag, not a route edit. */}
            <Route
              path="/referrals"
              element={REFERRALS_ENABLED ? <ReferralsPage /> : <Navigate to="/" replace />}
            />
            <Route path="/demo" element={<DemoPage onNavigate={navigate} />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/telegram" element={<SocialRedirect profile="telegram" />} />
            <Route path="/instagram" element={<SocialRedirect profile="instagram" />} />
            {/* Campaign short links (suikamon.com/yt …): the free starter page, tagged by channel. */}
            {CAMPAIGN_LINK_KEYS.map((key) => (
              <Route key={key} path={`/${key}`} element={<CampaignRedirect link={key} />} />
            ))}
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/pvp-screenshot" element={<PvpScreenshotPage />} />
            <Route path="/debug" element={<Navigate to="/admin" replace />} />
            <Route path="/metrics" element={<Navigate to="/admin" replace />} />
            <Route
              path="/adopt"
              // Web2: the game is free — /adopt shows who can be discovered; the paid,
              // minting AdoptPage is parked (still compiles, not routed).
              element={<DiscoverPage />}
            />
            {/* Media kit and brand assets - not linked in navigation */}
            <Route path="/media" element={<MediaPage />} />
            {/* Dev-only minigames test page - not linked in navigation */}
            <Route path="/minigames" element={<MinigamesTestPage />} />
            <Route path="/suikamoncss" element={<SuikamonCssPage />} />
            <Route path="/ai" element={null} />
            <Route path="/business-briefing" element={<BusinessBriefingPage />} />
            <Route path="/news" element={<NewsPage />} />
            <Route path="/news/:slug" element={<NewsArticlePage />} />
            <Route path="/collection" element={<CollectionPage />} />
            <Route path="/suikadex" element={<SuikadexPage />} />
            <Route path="/profile" element={<UserProfilePage />} />
            {/* Species pages - must be after all static routes but before catch-all */}
            <Route path="/:speciesName" element={<SpeciesPage onNavigate={navigate} />} />
            {/* A URL that does not exist says so, with `noindex` — never a silent
                redirect to the home page, which Google reads as a soft 404 and logs as a
                duplicate of the front door. */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
        </GameShell>
        {!hideFooter && (
        <>
        <footer className="suikamon-footer">
          <Flex direction="column" gap="5">
            <Flex className="suikamon-footer-top" justify="between" align="start" gap="5" wrap="wrap">
              <Flex direction="column" gap="2" className="suikamon-footer-brand">
                <Text as="p" size="5" weight="bold">Suikamon</Text>
                <Text size="2" className="suikamon-footer-copy">
                  {t("navigation.footer.tagline")}
                </Text>
                <LanguageSelector className="language-selector--footer" />
              </Flex>
              <Flex direction="column" gap="2" className="suikamon-footer-links">
                <Text size="2" className="suikamon-footer-heading">
                  {t("navigation.footer.play.heading")}
                </Text>
                <Flex direction="column" gap="1">
                  <button
                    type="button"
                    className="suikamon-footer-link"
                    onClick={() => navigate("battle-guide")}
                  >
                    {t("navigation.footer.battleGuide")}
                  </button>
                  <button
                    type="button"
                    className="suikamon-footer-link"
                    onClick={() => navigate("collection")}
                  >
                    {t("navigation.footer.collection")}
                  </button>
                </Flex>
              </Flex>
              <Flex direction="column" gap="2" className="suikamon-footer-links">
                <Text size="2" className="suikamon-footer-heading">
                  {t("navigation.footer.resources.heading")}
                </Text>
                <Flex direction="column" gap="1">
                  <button
                    type="button"
                    className="suikamon-footer-link"
                    onClick={() => navigate("/help")}
                  >
                    {t("navigation.footer.help")}
                  </button>
                  <button
                    type="button"
                    className="suikamon-footer-link"
                    onClick={() => navigate("/terms")}
                  >
                    {t("navigation.footer.terms")}
                  </button>
                  <button
                    type="button"
                    className="suikamon-footer-link"
                    onClick={() => navigate("/privacy")}
                  >
                    {t("navigation.footer.privacy")}
                  </button>
                  <button
                    type="button"
                    className="suikamon-footer-link"
                    onClick={resetAnalyticsConsent}
                  >
                    {t("navigation.footer.cookieSettings")}
                  </button>
                  <button
                    type="button"
                    className="suikamon-footer-link"
                    onClick={() => navigate("/news")}
                  >
                    {t("navigation.footer.news")}
                  </button>
                </Flex>
              </Flex>
              <Flex direction="column" gap="2" className="suikamon-footer-social">
                <Text size="2" className="suikamon-footer-heading">
                  {t("navigation.footer.social.heading")}
                </Text>
                <Flex align="center" gap="3" wrap="wrap" className="suikamon-footer-icons">
                  <a
                    href="https://x.com/suikamongame"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="suikamon-footer-icon"
                    aria-label={t("navigation.footer.social.x")}
                  >
                    <svg
                      aria-hidden="true"
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </a>
                  <a
                    href="https://discord.gg/W9ZBTpue7t"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="suikamon-footer-icon"
                    aria-label={t("navigation.footer.social.discord")}
                  >
                    <svg
                      aria-hidden="true"
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                    </svg>
                  </a>
                  <a
                    href="https://t.me/suikamongame"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="suikamon-footer-icon"
                    aria-label={t("navigation.footer.social.telegram")}
                  >
                    <svg
                      aria-hidden="true"
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                    </svg>
                  </a>
                </Flex>
              </Flex>
            </Flex>
            <Flex justify="between" align="center" gap="3" wrap="wrap" className="suikamon-footer-bottom">
              <Text size="2" className="suikamon-footer-copy">
                {t("navigation.footer.copyright", { year: String(new Date().getFullYear()), version: SUIKAMON_APP_VERSION })}
              </Text>
              <Text size="2" className="suikamon-footer-copy">
                {t("navigation.footer.motto")}
              </Text>
            </Flex>
          </Flex>
        </footer>
        </>
        )}
        {/* Reserves the sticky banner's height so it never covers the footer or page buttons. */}
        {showPlayBanner && <PlayFirstSuikamonSpacer />}
      </Container>
    </Flex>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
